import { I18n } from "aws-amplify/utils";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { grey } from "@mui/material/colors";
import { useTemplateController } from "context";
import { useNavigate } from "react-router-dom";
import LaunchIcon from "@mui/icons-material/Launch";
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { AlarmOffOutlined, PauseCircleFilledOutlined } from "@mui/icons-material";
import { format } from "date-fns";

const formatDateTime = (datetime) => {
  try {
    return format(new Date(datetime), "yyyy-MM-dd HH:mm:ss");
  } catch (error) {
    console.error("Invalid date format:", datetime);
    return datetime; // Ritorna il valore originale se non è una data valida
  }
};

const SeverityComponent = ({ alarm_type_description, color }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography>{alarm_type_description}</Typography>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: color, // Usa il colore dell'allarme o un valore di fallback
        }}
      />
    </Stack>
  );
};
const StateComponent = ({ value }) => {
  const [controller] = useTemplateController();
  const { darkMode } = controller;

  const stateColor = darkMode ? grey[200] : grey[800];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {value === "0" ? (
        <Stack direction="row">
          {"Inactive"}{" "}
          <CheckCircleIcon
            sx={{ ml: 1, mt:0.3, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "1" ? (
        <Stack direction="row">
          {"Active"}{" "}
          <WarningIcon
            sx={{ ml: 1, mt:0.3, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "2" ? (
        <Stack direction="row">
          {"Close"}{" "}
          <AlarmOffOutlined
            sx={{ ml: 1, mt:0.3, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "3" ? (
        <Stack direction="row">
          {"Suspended"}{" "}
          <PauseCircleFilledOutlined
            sx={{ ml: 1, mt:0.3, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "4" ? (
        <Stack direction="row">
          {"Suspended With Lock"}{" "}
          <PauseCircleOutlineIcon
            sx={{ ml: 1, mt:0.3, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : (
        <Stack direction="row">
          {"Unknown"}{" "}
          <WarningIcon
            sx={{ ml: 1, mt:0.3, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      )}
    </Box>
  );
};

const ActionComponent = ({ selectedRow }) => {
  const navigate = useNavigate();
  return (
    <Tooltip title="View Details">
    <IconButton
      onClick={() => navigate(`/dashboard/alarm-details/${selectedRow.id}`)}
    >
      <LaunchIcon />
    </IconButton>
    </Tooltip>
  );
};

export const getStoresColumns = () => [
  {
    id: "datetime_start",
    label: I18n.get("Start Date"),
    align: "left",
    flex: 1,
    rendercell: (params) => {
      if (params.rowNode?.type === "group") {
        return undefined;
      }
      return formatDateTime(params.value);
    },
  },
  {
    id: "duration",
    label: I18n.get("Duration"),
    align: "left",
    flex: 1,
  },
  {
    id: "alarm_note",
    label: I18n.get("Remark"),
    align: "left",
    flex: 1,
  },
  {
    id: "process_note",
    label: I18n.get("Process Note"),
    align: "left",
    flex: 1,
  },
  {
    id: "original_equipment",
    label: I18n.get("Original Equipment"),
    align: "left",
    flex: 1,
  },
  {
    id: "position",
    label: I18n.get("Position"),
    align: "left",
    flex: 1,
  },
  {
    id: "alarm_text",
    label: I18n.get("Alarm Text"),
    align: "left",
    flex: 1,
  },
  {
    id: "alarm_type_description",
    label: I18n.get("Alarm Type Description"),
    align: "left",
    flex: 1,
    rendercell: (params) => {
      if (params.rowNode?.type === "group") {
        return undefined;
      }
      const { alarm_type_description, color } = params.row;
      return <SeverityComponent alarm_type_description={alarm_type_description} color={color} />;
    },
  },
  {
    id: "state",
    label: I18n.get("State"),
    align: "left",
    flex: 1,
    rendercell: (params) => {
      if (params.rowNode?.type === "group") {
        return undefined;
      }
      return <StateComponent value={params.value} />;
    },
  },
];
